import axios from "axios"
import { APP_API_URL } from "./api-config.service"
import { flushPendingClientErrors, reportApiError, reportUnexpectedClientError } from './client-error.service'

let refreshTimeoutID: ReturnType<typeof setTimeout> | null = null
let refreshPromise: Promise<boolean> | null = null
const REFRESH_TIMEOUT_MS = 10000
const MAX_REFRESH_ATTEMPTS = 2
const AUTH_REJECTION_STATUS_CODES = new Set([400, 401, 403])
const RETRYABLE_REFRESH_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504])

const isRetryableRefreshError = (error: unknown) => {
    if (!axios.isAxiosError(error)) return false

    const status = Number(error.response?.status)
    if (status) return RETRYABLE_REFRESH_STATUS_CODES.has(status)

    return ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(error.code || "")
}

const clearTokensAndRedirect = () => {
    try {
        localStorage.removeItem("us_id")
        localStorage.removeItem("us_exp")
    } catch (error) {
        reportUnexpectedClientError(error, 'token.clearTokensAndRedirect')
    }
    window.location.href = "/"
}

const refreshTokenRequest = async (): Promise<boolean> => {
    if (refreshPromise) return refreshPromise

    refreshPromise = (async () => {
        for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt += 1) {
            try {
                const res = await axios.get(`${APP_API_URL}/auth/refresh`, {
                    withCredentials: true,
                    timeout: REFRESH_TIMEOUT_MS,
                })

                const response = res.data?.data

                if (response?.token && response?.tokenExpiration) {
                    localStorage.setItem("us_id", response.token)
                    localStorage.setItem("us_exp", response.tokenExpiration.toString())
                    flushPendingClientErrors()
                    scheduleRefresh(response.tokenExpiration)
                    return true
                }

                const responseStatus = Number(res.data?.status)
                if (AUTH_REJECTION_STATUS_CODES.has(responseStatus)) return false
                if (attempt < MAX_REFRESH_ATTEMPTS && RETRYABLE_REFRESH_STATUS_CODES.has(responseStatus)) continue

                throw new Error(res.data?.message || 'Сервер вернул некорректный ответ обновления сессии.')
            } catch (error) {
                if (axios.isAxiosError(error) && AUTH_REJECTION_STATUS_CODES.has(Number(error.response?.status))) {
                    return false
                }

                if (attempt < MAX_REFRESH_ATTEMPTS && isRetryableRefreshError(error)) continue

                if (axios.isAxiosError(error)) reportApiError(error)
                else reportUnexpectedClientError(error, 'token.refreshTokenRequest')
                throw error
            }
        }

        return false
    })().finally(() => {
            refreshPromise = null
    })

    return refreshPromise
}

const scheduleRefresh = (expiration: number) => {
    const timeoutTrigger = expiration - 20000
    if (timeoutTrigger <= 0) return

    if (refreshTimeoutID) clearTimeout(refreshTimeoutID)

    refreshTimeoutID = setTimeout(async () => {
        try {
            const ok = await refreshTokenRequest()
            if (!ok) clearTokensAndRedirect()
        } catch (error) {
            reportUnexpectedClientError(error, 'token.scheduleRefresh')
        }
    }, timeoutTrigger)
}

const abortRefreshToken = () => {
    if (refreshTimeoutID) {
        clearTimeout(refreshTimeoutID)
        refreshTimeoutID = null
    }
}

export const tokenService = {
    scheduleRefresh,
    abortRefreshToken,
    clearTokensAndRedirect,
    refreshTokenRequest,
}